import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';

import { usePresentationTemplateEditorContext } from '../../../PresentationTemplateEditor.context';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorFormShape,
} from '../../../PresentationTemplateEditor.types';
import {
  EntitySummaryPagesSelection,
  EntityTypesMultiSelect,
} from './EntitySummaryBundleConfigModal.components';
import {
  buildEntitySummaryBundleFromForm,
  EntitySummaryBundleConfigFormShape,
  getDefaultEntitySummaryValues,
  isEntitySummaryConfigIncomplete,
} from './EntitySummaryBundleConfigModal.utils';

export interface EntitySummaryBundleConfigModalProps {
  /** null for create mode, index for edit mode */
  bundleIndex: number | null;
  onClose: () => void;
}

/**
 * Reads the parent form's bundle data (for edit mode), then renders the modal
 * with its own nested FormProvider to avoid polluting the parent form.
 */
export function EntitySummaryBundleConfigModal({
  bundleIndex,
  onClose,
}: EntitySummaryBundleConfigModalProps) {
  const { control } = useFormContext<PresentationTemplateEditorFormShape>();

  // Read existing bundle data for edit mode before we shadow the parent form context
  const existingBundle = useWatch({
    control,
    name:
      bundleIndex !== null
        ? (`bundles.${bundleIndex}` as const)
        : // Watch a field that won't match to get undefined in create mode
          (undefined as never),
  }) as PresentationTemplateEditorBundle | undefined;

  const isEditMode = bundleIndex !== null;

  return (
    <EntitySummaryBundleConfigModalInner
      existingBundle={isEditMode ? existingBundle : undefined}
      bundleIndex={bundleIndex}
      onClose={onClose}
    />
  );
}

interface EntitySummaryBundleConfigModalInnerProps {
  existingBundle?: PresentationTemplateEditorBundle;
  bundleIndex: number | null;
  onClose: () => void;
}

function EntitySummaryBundleConfigModalInner({
  existingBundle,
  bundleIndex,
  onClose,
}: EntitySummaryBundleConfigModalInnerProps) {
  const { bundlesFormArray, markBundlesDirty } =
    usePresentationTemplateEditorContext();
  const isEditMode = bundleIndex !== null;

  const form = useForm<EntitySummaryBundleConfigFormShape>({
    defaultValues: getDefaultEntitySummaryValues(existingBundle),
  });

  const canSave = useCanSaveEntitySummaryConfig(form.control);

  const handleSave = useCallback(() => {
    const bundle = buildEntitySummaryBundleFromForm(
      form.getValues(),
      existingBundle
    );

    if (isEditMode && bundleIndex !== null) {
      bundlesFormArray.update(bundleIndex, bundle);
      // useFieldArray.update() doesn't propagate dirty state to the parent
      // form, so flip isDirty explicitly to keep the Save button enabled.
      markBundlesDirty();
    } else {
      bundlesFormArray.append(bundle);
    }

    onClose();
  }, [
    form,
    existingBundle,
    isEditMode,
    bundleIndex,
    bundlesFormArray,
    markBundlesDirty,
    onClose,
  ]);

  const actions = useMemo(
    () => (
      <Stack direction="row" justifyContent="flex-end" spacing={2} width="100%">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
        >
          {isEditMode ? 'Save' : 'Add entity overview'}
        </Button>
      </Stack>
    ),
    [onClose, handleSave, canSave, isEditMode]
  );

  return (
    <FormProvider {...form}>
      <FormModal
        heading={isEditMode ? 'Edit entity overview' : 'Add entity overview'}
        isOpen
        onClose={onClose}
        actions={actions}
        rightHeaderContent={null}
      >
        <Stack direction="column" spacing={2}>
          <EntityTypesMultiSelect control={form.control} />

          <EntitySummaryPagesSelection control={form.control} />
        </Stack>
      </FormModal>
    </FormProvider>
  );
}

/**
 * Whether the current selection can produce usable output. Mirrors the tile's
 * "review and edit" check so a bundle can never be saved into the state that
 * check flags.
 */
function useCanSaveEntitySummaryConfig(
  control: ReturnType<
    typeof useForm<EntitySummaryBundleConfigFormShape>
  >['control']
): boolean {
  const [
    entityTypes,
    includeEntitySummaryPage,
    includeDiagramAndNotesPage,
    includeBalanceSheetPage,
    includeBalanceSheetExportPage,
    includeBeneficiariesAndDispositionsPage,
    includeInsurancePolicySlides,
  ] = useWatch({
    control,
    name: [
      'entityTypes',
      'includeEntitySummaryPage',
      'includeDiagramAndNotesPage',
      'includeBalanceSheetPage',
      'includeBalanceSheetExportPage',
      'includeBeneficiariesAndDispositionsPage',
      'includeInsurancePolicySlides',
    ],
  });

  return !isEntitySummaryConfigIncomplete({
    entityTypes,
    includeEntitySummaryPage,
    includeDiagramAndNotesPage,
    includeBalanceSheetPage,
    includeBalanceSheetExportPage,
    includeBeneficiariesAndDispositionsPage,
    includeInsurancePolicySlides,
  });
}
